<script setup lang="ts">
import { computed, onMounted, onUnmounted, type PropType, ref, VNode } from "vue";
import { DesignVersion, Placement } from "@/models/types";
import { MenuEntry } from "@/models/menu-entry";
import BcButton from "../bc-button.vue";
import BcTooltip from "../bc-tooltip.vue";
import BcMenu from "./bc-menu.vue";

defineOptions({
  name: "BcPopupMenu",
  components: { BcButton, BcTooltip, BcMenu },
});

const props = defineProps({
  /** Closes menu on item select */
  closeOnSelect: { type: Boolean, default: true },
  /** Specifies the timeout to wait before close the popup */
  closeTimeout: {
    type: Number,
    default: 300,
    validator: (value: any) => typeof value === "number" && value >= 100,
  },
  /** Defines the popup's style */
  design: {
    type: String as PropType<DesignVersion>,
    default: "6",
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  /** Disabled the popup control */
  disabled: { type: Boolean, default: false },
  disableHeightCalculation: { type: Boolean, default: false },
  /** Sets a divider between items */
  separateAll: { type: Boolean, default: true },
  /** Specifies the selectable items */
  items: {
    type: Array as PropType<Array<MenuEntry>>,
  },
  /** Specifies the placement of the menu items compared to the container element */
  placement: {
    type: String as PropType<Placement>,
    default: "bottom-start",
    validator: (value: any) => Placement.isPlacement(value),
  },
  subItemIcon: { type: Object as PropType<VNode> },
  /** Specifies the title for the popup */
  title: { type: String },
  textWrap: { type: Boolean, default: false },
  offset: { type: Number, default: 3 },
  teleportTarget: { type: String },
});

const pak = computed<string>(() => {
  return "bc-controls-d" + (props.design == "6" ? "6" : "7");
});

const emit = defineEmits<{
  (e: "trigger"): void;
  (e: "select", payload: MenuEntry): void;
}>();

const showPopup = ref(false);
/** Popup menu reference */
const popupMenuRef = ref<HTMLDivElement>();
/** Popup trigger reference */
const popupTriggerRef = ref<HTMLDivElement>();

/** Specifies the evaluated expression to prevent (if needed) */
const hideLastCall = ref<any>(null);

function clearTimeoutMethod(): void {
  if (hideLastCall.value) {
    clearTimeout(hideLastCall.value ?? undefined);
  }
}

/** Handling button's `onclick` event */
const triggerPopup = () => {
  if (props.disabled) return;

  emit("trigger");
  showPopup.value = !showPopup.value;
};

/**
 * Notifying parent component if an item is clicked.
 * Item's value will be emitted to the parent.
 * @param {ListItem} item clicked item
 */
function selectItem(item: MenuEntry): void {
  if (props.disabled || item.disabled) return;
  /** When popup list item is selected this event is fired. */
  emit("select", item);
  if (props.closeOnSelect) {
    clearTimeoutMethod();
    showPopup.value = false;
  }
}

/** Hides popup */
const hide = () => {
  clearTimeoutMethod();

  hideLastCall.value = window.setTimeout(() => {
    showPopup.value = false;
  }, Math.max(100, props.closeTimeout));
};

function hideOnClose(event: MouseEvent) {
  if (
    popupMenuRef.value &&
    event.target &&
    popupMenuRef.value !== event.target &&
    !popupMenuRef.value.contains(event.target as Node)
  ) {
    clearTimeoutMethod();
    showPopup.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", hideOnClose);
});

onUnmounted(() => {
  document.removeEventListener("click", hideOnClose);
});

defineExpose({
  popupTriggerRef,
  showPopup,
  triggerPopup,
  selectItem,
  hide,
});
</script>

<template>
  <div
    :data-pak="pak"
    :data-design="design"
    class="bissantzControls popupMenuControl"
    :class="{ disabled: disabled }"
    ref="popupMenuRef"
    @mouseenter="clearTimeoutMethod"
    @mouseleave="hide"
  >
    <bc-tooltip
      :mouse-mode="design !== '6'"
      :content-design="design === '6' ? 'dark' : 'mouseMode'"
      :content="disabled || design === '6' ? '' : title"
    >
      <div
        ref="popupTriggerRef"
        class="popupTrigger"
        :class="{
          defaultTrigger: !$slots['popup-trigger'],
        }"
        :title="design === '6' ? title : undefined"
        @click="triggerPopup"
      >
        <slot name="popup-trigger">
          <bc-button :design="design" />
        </slot>
      </div>
    </bc-tooltip>

    <bc-menu
      :design="design"
      :disableHeightCalculation="disableHeightCalculation"
      :items="items"
      :isVisible="showPopup"
      :parentRect="popupTriggerRef"
      :placement="placement"
      :separateAll="separateAll"
      :subItemIcon="subItemIcon"
      :textWrap="textWrap"
      :offset="offset"
      :teleportTarget="teleportTarget"
      @click="selectItem($event)"
      @mouseenter="clearTimeoutMethod"
      @mouseleave="hide"
    >
      <slot name="menu-items"></slot>
    </bc-menu>
  </div>
</template>

<style lang="less" scoped>
.bissantzControls.popupMenuControl {
  display: inline-block;
  position: relative;
  width: fit-content;
  height: fit-content;

  .popupTrigger {
    cursor: pointer;
  }

  .popupTrigger:not(.defaultTrigger) {
    stroke: var(--popup-trigger-icon-color);
    fill: var(--popup-trigger-icon-color);
  }

  .popupTrigger:not(.defaultTrigger):hover {
    color: var(--input-primary);
    stroke: var(--input-primary);
    fill: var(--input-primary);
  }

  .popupTrigger.defaultTrigger button {
    min-width: 20px;
    min-height: 20px;
  }

  &.disabled .popupTrigger:not(.defaultTrigger) {
    color: var(--input-disabled-color);
    stroke: var(--input-disabled-color);
    fill: var(--input-disabled-color);
  }

  .bissantzControls.buttonControl {
    border: none;
  }
}
</style>
